import { createInertiaApp, router } from "@inertiajs/react"
import { ReactNode } from "react"
import { createRoot, hydrateRoot } from "react-dom/client"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import registerServiceWorker from "@/utils/registerServiceWorker"
import { initSentry, Sentry } from "@/lib/sentry"
import { initPostHog, identifyUser, capturePageview } from "@/lib/posthog"
import { syncDocumentLanguage } from "@/utils/documentLanguage"

// Initialize observability before app mount
initSentry()
initPostHog()

const queryClient = new QueryClient()

// Temporary type definition, until @inertiajs/react provides one
export type ResolvedComponent = {
  default: ReactNode
  layout?: (page: ReactNode) => ReactNode
}

// Track SPA pageviews on Inertia navigation
router.on("navigate", (event) => {
  syncDocumentLanguage(event.detail.page.props)
  capturePageview()
})

createInertiaApp({
  title: (title) => title || "Your personal online chess database — ChessDrop",

  progress: {
    color: "#FF3F1E",
    showSpinner: false,
  },

  resolve: (name) => {
    const pages = import.meta.glob<ResolvedComponent>("../pages/**/*.tsx", {
      eager: true,
    })

    const page = pages[`../pages/${name}.tsx`]

    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
    }

    return page
  },

  setup({ el, App, props }) {
    if (el) {
      // Identify user for PostHog + Sentry if logged in
      const initialPage = props.initialPage
      syncDocumentLanguage(initialPage.props)
      const auth = (initialPage.props as Record<string, unknown>).auth as
        | { user?: { id: string; email: string } }
        | undefined
      if (auth?.user) {
        identifyUser(auth.user)
        Sentry.setUser({ id: auth.user.id, email: auth.user.email })
      }

      const tree = (
        <Sentry.ErrorBoundary fallback={<p>Something went wrong.</p>}>
          <QueryClientProvider client={queryClient}>
            <App {...props} />
          </QueryClientProvider>
        </Sentry.ErrorBoundary>
      )

      const shouldHydrate = Boolean(el.firstElementChild)

      if (shouldHydrate) {
        hydrateRoot(el, tree)
      } else {
        createRoot(el).render(tree)
      }

      registerServiceWorker()
    } else {
      console.error(
        "Missing root element.\n\n" +
          "If you see this error, it probably means you load Inertia.js on non-Inertia pages.\n" +
          'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.'
      )
    }
  },
})
